import {AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {debounceTime, switchMap, tap} from 'rxjs/operators';
// TODO: Remove all instances of UtilsService and take approach of seeing if any MatDialog's are open
// import {UtilsService} from '../../services/utils.service';
import {IAutoCompleteItem} from './iautocompleteitem';

@Component({
    selector: 'app-kp-autocomplete',
    templateUrl: 'auto-complete.component.html',
    styleUrls: ['auto-complete.component.scss'],
    standalone: false
})
export class AutoCompleteComponent<T> implements AfterViewInit {

  @ViewChild('searchInput') searchInput: ElementRef | undefined;

  @Input()
  filterFunction: ((value: string) => Observable<IAutoCompleteItem<T>[]>) | undefined;

  @Input()
  placeholder = '';

  @Input()
  focusInputOnLoad = false;

  @Input()
  disableAutoDropdownSelection = false;

  @Input()
  disabled = false;

  @Input()
  valid = true;

  @Input()
  position: 'absolute' | 'initial' = 'absolute';

  @Input()
  embeddedDropDown = false;

  @Input()
  clearOnSelection = false;

  @Input()
  allowCustomInput = false;

  private selectedItem: IAutoCompleteItem<T> | undefined;

  @Input()
  set selected(item: IAutoCompleteItem<T> | undefined) {
    this.selectedItem = item;
    this.selectedValue = item?.label ?? '';
    setTimeout(() => this.applySelectedValueToInput(), 0);
  }

  get selected(): IAutoCompleteItem<T> | undefined {
    return this.selectedItem;
  }

  @Output()
  selectedChange = new EventEmitter<T>();

  @Output()
  customEntry = new EventEmitter<string>();

  items$: Observable<IAutoCompleteItem<T>[]>;
  items: IAutoCompleteItem<T>[] = [];
  hide = false;
  highlightItemIndex = -1;
  selectedValue = '';

  searching: boolean | undefined;
  addingChip = false;

  private valueChangesSubject = new Subject<string>();
  private valueChanges$ = this.valueChangesSubject.asObservable();
  private itemHeight = 0;
  private visibleItemCount = 0;
  private keyHandlers: {[index: string]: () => void} = {
    ArrowDown: this.arrowKeyDownHandler.bind(this),
    ArrowUp: this.arrowKeyUpHandler.bind(this),
    Enter: this.enterKeyHandler.bind(this),
    Escape: this.escapeKeyHandler.bind(this)
  };
  private suppressDefaultKeyBehaviours = ['ArrowUp', 'ArrowDown'];
  private ignoreDocumentClick = false;

  @ViewChild('dropDownContainer')
  dropdownContainer: ElementRef | undefined;

  @ViewChild('searchBox') searchBox: ElementRef | undefined;
  @ViewChild('clearSearch') clearSearch: ElementRef | undefined;

  constructor(/*private utilsService: UtilsService*/) {
    this.items$ = this.valueChanges$.pipe(
      debounceTime(400),
      tap(_ => this.searching = true),
      switchMap(value => this.filterFunction ? this.filterFunction(value) : of([])),
      tap(_ => this.searching = false),
      tap(result => {
        // if (this.utilsService.dialogOpen())
        //   this.utilsService.escapableElementActive(true);
        this.items = result;
        if (!this.disableAutoDropdownSelection) {
          this.highlightItemIndex = result.length > 0 ? 0 : -1;
        } else if (!result.length) {
          this.highlightItemIndex = -1;
        }
        this.calculateDropdownItemDimensions();
        this.scrollResultsIntoView();
      })
    );
  }

  @HostListener('click')
  componentClick() {
    this.ignoreDocumentClick = true;
  }

  @HostListener('document:click')
  documentClick() {
    if (!this.ignoreDocumentClick) {
      this.hide = true;
    }
    this.ignoreDocumentClick = false;
  }

  // Keyboard Shortcuts - Use Shift + Ctrl + F to Search and Esc to clear
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    const searchBox = this.searchBox?.nativeElement;
    const clearSearch = this.clearSearch?.nativeElement;

    if (event.code === 'Escape' && this.clearSearch)
      clearSearch.click();

    // if (!this.utilsService.dialogOpen() && !this.utilsService.escapableElementActive() && (event.ctrlKey && event.shiftKey)) {
    //   if (event.code === 'KeyF') {
    //     searchBox.focus();
    //     searchBox.select();
    //   }
    // }
  }

  ngAfterViewInit(): void {
    if (this.focusInputOnLoad)
      this.searchInput?.nativeElement.focus();
  }

  private applySelectedValueToInput(): void {
    if (this.searchInput?.nativeElement && this.selectedValue) {
      this.searchInput.nativeElement.value = this.selectedValue;
    }
  }

  //region UI Event Handlers
  input(e: any): void {
    this.hide = false;    // tap in the value change?
    this.valueChangesSubject.next(e.target.value);
  }

  keydown(e: any): void {
    if (e.repeat) {
      if (e.key === 'ArrowDown')
        this.arrowKeyDownHandler();
      if (e.key === 'ArrowUp')
        this.arrowKeyUpHandler();
    }
    // TODO: When the utilsService approach is looked at (don't bring it over - we can just look at if any MatDialogs are currently open), this can probably just go
    // if (this.items.length === 0 && this.searchBox.nativeElement.value.length === 0) {
    //   this.utilsService.escapableElementActive(false);
    // }

    // e.g. prevent caret from jumping to start / end of text
    if (this.suppressDefaultKeyBehaviours.findIndex(key => e.key === key) > -1)
      e.preventDefault();
  }

  keyup(e: any): void {
    const handler = this.keyHandlers[e.key];
    if (handler) {
      handler();
      e.preventDefault();
    }
  }

  blur(_e: any): void {
    // Blur fires when hitting Esc. Intention: Hitting Esc while focussed on input will clear the value.
    // Clicking/tabbing out then hitting Esc will close dialog if open
    // setTimeout(_ => {
    //   this.utilsService.escapableElementActive(false);
    // }, 200);
  }

  focus(e: any): void {
    setTimeout(() => this.applySelectedValueToInput(), 0);
  }

  click(e: any): void {
    this.valueChangesSubject.next(e.target.value);
    this.hide = false;
    setTimeout(() => this.applySelectedValueToInput(), 0);
  }

  select(e: IAutoCompleteItem<T>): void {
    this.clearOnSelection ? this.selectedValue = '' : this.selectedValue = e.label;
    this.searchInput!.nativeElement.value = this.selectedValue;
    this.hide = true;
    this.items = [];
    this.highlightItemIndex = -1;
    this.selectedChange.emit(e.data);
  }

  addCustomEntry(customValue: string, iconClick?: boolean): void {
    if (this.allowCustomInput && (this.highlightItemIndex === -1 || iconClick)) {
      this.customEntry.emit(customValue);
      this.clearOnSelection ? this.selectedValue = '' : this.selectedValue = customValue;
      this.searchInput!.nativeElement.value = this.selectedValue;
      this.addingChip = true;
      setTimeout(() => { this.addingChip = false; }, 500);
    }
  }

  clearSelection(): void {
    this.clearInput(this.searchInput?.nativeElement, false);
  }

  clearInput(inputElement: HTMLElement, focusInput: boolean): void {
    const input = inputElement as HTMLInputElement;
    input.value = '';
    this.selectedValue = '';
    if (focusInput)
      inputElement.focus();
    // if (this.items.length > 0)
    //   this.utilsService.escapableElementActive(false);
    this.items = [];
    this.valueChangesSubject.next(input.value);
  }
  //endregion

  //region Private Functions
  private arrowKeyDownHandler(): void {
    const index = this.highlightItemIndex + 1;
    this.highlightItemIndex = index < this.items.length ? index : this.items.length - 1;
    if (index !== this.highlightItemIndex)
      return;

    const highlightY = this.highlightItemIndex * this.itemHeight;
    const scrollTop = this.dropdownContainer?.nativeElement.scrollTop;
    const clientHeight = this.visibleItemCount * this.itemHeight;
    if (highlightY < scrollTop || highlightY > (scrollTop + clientHeight))
      this.dropdownContainer?.nativeElement.scrollTo(0, this.highlightItemIndex * this.itemHeight);
    else if (highlightY + this.itemHeight > scrollTop + clientHeight) {
      // make sure the selected item is entirely on screen when it is at the end of the visible range
      const diff = (highlightY + this.itemHeight) - (scrollTop + clientHeight);
      this.dropdownContainer?.nativeElement.scrollBy(0, diff);
    }
  }

  private arrowKeyUpHandler(): void {
    const index = this.highlightItemIndex - 1;
    this.highlightItemIndex = index > -1 ? index : -1;
    if (index !== this.highlightItemIndex)
      return;

    const highlightY = this.highlightItemIndex * this.itemHeight;
    const scrollTop = this.dropdownContainer?.nativeElement.scrollTop;
    const clientHeight = this.visibleItemCount * this.itemHeight;
    if (highlightY < scrollTop || highlightY > (scrollTop + clientHeight))
      this.dropdownContainer?.nativeElement.scrollTo(0, this.highlightItemIndex * this.itemHeight);
  }

  private enterKeyHandler(): void {
    if (this.highlightItemIndex > -1)
      this.select(this.items[this.highlightItemIndex]);
    this.hide = true;
  }

  private escapeKeyHandler(): void {
    this.hide = true;
  }

  private calculateDropdownItemDimensions(): void {
    setTimeout(() => {
      if (this.items.length === 0) {
        this.itemHeight = 0;
        this.visibleItemCount = 0;
        return;
      }

      this.itemHeight = this.dropdownContainer?.nativeElement.childNodes[0].clientHeight;
      // tslint:disable-next-line:no-bitwise
      this.visibleItemCount = (this.dropdownContainer?.nativeElement.clientHeight / this.itemHeight) | 0;
    });
  }

  private scrollResultsIntoView(): void {
    setTimeout(_ => {this.dropdownContainer?.nativeElement.scrollIntoView({block: 'center', behavior: 'smooth'}); }, 0);
  }

  //endregion

}
